<template>
    <ValidationObserver
        ref="formSaveSearch"
        tag="form"
        class="afu-grid afu-grid-cols-1"
        @submit.prevent="onSave()"
    >
        <div v-show="!isLogin">
            <ValidationProvider
                name="email address"
                rules="required|email"
                v-slot="{ errors }"
            >
                <C1FormGroup>
                    <C1Label class="afu-font-bold">
                        <span>{{ $t('label.email_address') }}</span>
                    </C1Label>

                    <C1FormInput
                        id="email"
                        v-model="form.email"
                        :is-invalid="!!errors[0]"
                        :invalid-feedback="errors[0]"
                        name="email"
                        type="email"
                        :placeholder="$t('label.email_address')"
                    />
                </C1FormGroup>
            </ValidationProvider>
        </div>
        <div>
            <ValidationProvider
                v-slot="{ errors }"
                name="alert name"
                rules="required"
                tag="div"
            >
                <C1FormGroup>
                    <C1Label class="afu-font-bold">
                        {{ $t('label.alert_name') }}
                    </C1Label>
                    <C1FormInput
                        v-model="form.search_name"
                        :is-invalid="!!errors[0]"
                        :invalid-feedback="errors[0]"
                        name="search_name"
                        :placeholder="$t('label.alert_name_placeholder')"
                    />
                </C1FormGroup>
            </ValidationProvider>
        </div>

        <div v-if="getSearchKeyword">
            <C1Label class="afu-font-bold">
                {{ $t('job_alert.search_term') }}
            </C1Label>
            <ul class="af-search-save-filters af-search-save-terms afu-list-none afu-m-0 afu-p-0">
                <li>
                    "{{ getSearchKeyword }}"
                </li>
            </ul>
        </div>

        <div v-if="getDisplayLocationLabel">
            <C1Label class="afu-font-bold">
                {{ $t('label.location') }}
            </C1Label>
            <ul class="af-search-save-terms">
                <li>
                    {{ getDisplayLocationLabel }}
                </li>
            </ul>
        </div>

        <div v-if="$_searchFilterTags.length">
            <C1Label class="afu-font-bold">
                {{ $t('label.filters') }}
            </C1Label>
            <ul class="af-search-save-filters">
                <li
                    v-for="(filterTag, index) in $_searchFilterTags"
                    :key="index"
                >
                    {{ filterTag.value }}
                </li>
            </ul>
        </div>

        <C1FormGroup v-if="$_searchFilterTags.some( ele => ele.label === 'salary')">
            <C1Label class="afu-font-bold">
                <!-- <FontAwesomeIcon :icon="['far', 'bell']" /> -->
                {{ $t('label.null_pay_included') }}
            </C1Label>

            <C1RadioButton
                :options="null_pay_included_options"
                :current-value="null_pay_included"
            />
        </C1FormGroup>

        <C1FormGroup>
            <C1Label class="afu-font-bold">
                <!-- <FontAwesomeIcon :icon="['far', 'bell']" /> -->
                {{ $t('label.email_alert_frequency') }}
            </C1Label>

            <Frequency
                :current-frequency="form.selected_frequency"
                @updateFrequency="updateFrequency"
            />
        </C1FormGroup>

        <AfTerms
            v-if="!$_me.email"
            :terms="getTerms"
        />

        <div class="afu-grid afu-grid-cols-2 afu-gap-2 afu-mt-5">
            <C1Button
                variant="outline-secondary"
                @click.stop.prevent="onClose()"
            >
                {{ $t('label.cancel') }}
            </C1Button>
            <C1Button
                variant="outline-primary"
                :is-busy="spinner"
                @click.stop.prevent="onSave()"
            >
                {{ $t('label.save') }}
            </C1Button>
        </div>
    </ValidationObserver>
</template>

<script>
    import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';

    import Frequency from '@/components/EmailAlerts/Frequency';
    import C1RadioButton from '@/components/C1Components/C1RadioButton';
    import C1Button from '@/components/C1Components/C1Button';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Label from '@/components/C1Components/C1Label';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import AfTerms from '@/components/Partials/AfTerms';

    export default {
        name: 'SaveSearchForm',

        components: {
            C1Label,
            C1FormInput,
            C1Button,
            C1RadioButton,
            ValidationObserver,
            ValidationProvider,
            Frequency,
            C1FormGroup,
            AfTerms,
        },

        props: {
            isLogin: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                form: {
                    email: '',
                    search_name: '',
                    selected_frequency: 'DAILY',
                },
                spinner: false,
                null_pay_included: { value: false },
                null_pay_included_options: [
                    { label: 'No', value: false },
                    { label: 'Yes', value: true },
                ],
            };
        },

        watch: {
            '$_me.email': {
                handler() {
                    this.form.email = this.$_me.email;
                },
                deep: true,
            },
        },

        mounted() {
            if (this.$_me.email) {
                this.form.email = this.$_me.email;
            }
        },

        computed: {
            ...mapState({
                $_searchPayload: state => state.search.searchPayload,
                $_me: state => state.me.user,
            }),

            ...mapGetters('search', {
                $_searchFilterTags: 'getSearchFilterTags',
            }),

            getDisplayLocationLabel() {
                return this.$_searchPayload.search_filters.location
                    ? this.$_searchPayload.search_filters.location.display_label
                    : '';
            },

            getSearchKeyword() {
                return this.$_searchPayload.search_filters.search;
            },

            getTerms() {
                let terms = window?.afConfig?.terms_content;
                if (terms) {
                    terms = JSON.parse(terms);
                } else {
                    terms = [];
                }
                return terms;
            },
        },

        async created() {
            this.form.search_name = this.$store.state.search.searchPayload.meta.h1;
            await this.$recaptchaLoaded();
        },

        methods: {
            ...mapMutations('search', {
                $_SET_SEARCH: 'SET',
            }),

            ...mapActions('me', {
                $_storeSavedSearch: 'storeSavedSearch',
            }),

            ...mapActions('user', {
                $_saveJobAlert: 'saveJobAlert',
            }),

            async onSave() {
                const validator = await this.$refs.formSaveSearch.validate();
                if (!validator) {
                    return false;
                }

                const token = await this.$recaptcha('job_alert');
                if (!token) {
                    return;
                }

                const filter = {
                    ...this.$_searchPayload.search_filters,
                    null_pay_included: this.null_pay_included.value,
                };

                const saveSearchdata = {
                    email: this.form.email,
                    name: this.form.search_name,
                    filter: filter,
                    frequency: this.form.selected_frequency,
                    search_url: this.$_searchPayload.meta.url,
                    token: token,
                };

                this.spinner = true;
                this.$_saveJobAlert(saveSearchdata).finally(() => {
                    this.spinner = false;
                    this.$emit('saveComplete');
                });
            },

            onClose() {
                this.$emit('close');
            },

            updateFrequency(frequency) {
                this.form.selected_frequency = frequency;
            },
        },
    };
</script>
